import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, Users, Gauge, ScrollText, CreditCard, AlertOctagon,
  Plus, Mail, Trash2, KeyRound, Eye, Save, Power, Wallet, Phone, Pencil,
  UsersRound, CheckCircle2, XCircle, HelpCircle
} from "lucide-react";
import { UsageTab } from "./company/UsageTab";
import { NumbersTab } from "./company/NumbersTab";
import {
  changeTenantPack, compTenant, deleteTenant, endImpersonation, extendTrial,
  fetchAudit, fetchTenant, fetchUsers, inviteUser, isNearLimit, markInvoicePaid,
  overrideTenantLimits, removeUser, sendPasswordReset, startImpersonation,
  suspendTenant, unsuspendTenant, updateTenant, updateUserRole,
  listDebitOrders, retryDebitOrder, markDebitOrderPaid, setTenantHint,
  setTenantMonthlyAmount, bulkInviteReps, resendUserInvite,
  fetchTenantFlags, setTenantFlag, resetTenantFlag
} from "../services/api";
import type { BulkInviteSummary, TenantFlag } from "../services/api";
import type {
  AuditEvent, DebitOrder, PlanLimits, Tenant, TenantUser, TenantUserRole
} from "../types";
import { PLANS, formatZAR } from "../services/plans";
import { usePacks, formatPackPrice, isClientPack, isSellable, packIncludes, packExcludes } from "../services/packs";
import type { PackOption } from "../services/api";
import { PackTag } from "../components/PackTag";
import { StatusPill } from "../components/StatusPill";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import { usePrompt } from "../components/PromptDialog";
import { usePageTitle } from "../hooks/usePageTitle";

type Tab = "overview" | "users" | "numbers" | "usage" | "plan" | "billing" | "audit" | "danger";

export function CompanyDetailPage() {
  usePageTitle("Company");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const prompt = usePrompt();
  const [tab, setTab] = useState<Tab>("overview");
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);

  async function reload() {
    if (!id) return;
    const [t, u, a] = await Promise.all([
      fetchTenant(id), fetchUsers(id), fetchAudit({ tenantId: id })
    ]);
    setTenant(t);
    setUsers(u);
    setAudit(a);
  }
  useEffect(() => {
    setTenantHint(id ?? null);
    reload();
    return () => setTenantHint(null);
    /* eslint-disable-next-line */
  }, [id]);

  async function renameCompany() {
    if (!tenant) return;
    const next = await prompt({
      title: "Rename company",
      label: "Company name",
      defaultValue: tenant.name,
      placeholder: "Company name",
      confirmLabel: "Save",
      required: true
    });
    if (next === null) return; // cancelled
    const name = next.trim();
    if (name.length < 2) {
      toast.push("error", "Company name must be at least 2 characters.");
      return;
    }
    if (name === tenant.name) return; // unchanged
    try {
      await updateTenant(tenant.id, { name });
      toast.push("success", "Company name updated.");
      reload();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't rename the company.");
    }
  }

  if (!tenant) return <div className="muted">Loading…</div>;

  return (
    <div className="page">
      <Link to="/companies" className="muted" style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
        <ArrowLeft size={14} /> Companies
      </Link>

      <header className="page-header">
        <div>
          <div className="row" style={{ gap: 10 }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, background: "var(--gradient-1)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Building2 size={20} color="#fff" />
            </span>
            <div>
              <div className="row" style={{ gap: 8, alignItems: "center" }}>
                <h1 style={{ marginBottom: 2 }}>{tenant.name}</h1>
                <button className="icon-btn" title="Rename company" onClick={renameCompany} style={{ color: "var(--text-mute)" }}>
                  <Pencil size={15} />
                </button>
              </div>
              <div className="row" style={{ gap: 8, fontSize: 13, color: "var(--text-mute)" }}>
                <span className="mono">{tenant.slug}</span>
                <span>·</span>
                <span>{tenant.industry}</span>
                <span>·</span>
                <span>{tenant.country}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <StatusPill status={tenant.status} />
          <PackTag pack={tenant.pack} />
          {isNearLimit(tenant) && <span className="pill warn">Near limit</span>}
          <button
            className="btn btn-ghost btn-sm"
            onClick={async () => {
              const reason = await prompt({
                title: "Impersonate tenant",
                message: <>You&rsquo;ll act as <strong>{tenant.name}</strong> for 30 minutes. This session is audited.</>,
                label: "Reason for impersonation",
                placeholder: "e.g. Investigating a billing discrepancy",
                confirmLabel: "Start impersonation",
                required: true
              });
              if (!reason) return;
              await startImpersonation(tenant.id, reason);
              toast.push("info", `Impersonating ${tenant.name} for 30 minutes.`);
              navigate(0);
            }}
          >
            <Eye size={14} /> Impersonate
          </button>
        </div>
      </header>

      <div className="tabs">
        <button className={`tab ${tab === "overview" ? "active" : ""}`} onClick={() => setTab("overview")}><Building2 size={14} /> Overview</button>
        <button className={`tab ${tab === "users" ? "active" : ""}`} onClick={() => setTab("users")}><Users size={14} /> Employees</button>
        <button className={`tab ${tab === "numbers" ? "active" : ""}`} onClick={() => setTab("numbers")}><Phone size={14} /> Numbers</button>
        <button className={`tab ${tab === "usage" ? "active" : ""}`} onClick={() => setTab("usage")}><Wallet size={14} /> Usage & cost</button>
        <button className={`tab ${tab === "plan" ? "active" : ""}`} onClick={() => setTab("plan")}><Gauge size={14} /> Package & limits</button>
        <button className={`tab ${tab === "billing" ? "active" : ""}`} onClick={() => setTab("billing")}><CreditCard size={14} /> Billing</button>
        <button className={`tab ${tab === "audit" ? "active" : ""}`} onClick={() => setTab("audit")}><ScrollText size={14} /> Audit</button>
        <button className={`tab ${tab === "danger" ? "active" : ""}`} onClick={() => setTab("danger")}><AlertOctagon size={14} /> Danger</button>
      </div>

      {tab === "overview" && <OverviewTab tenant={tenant} users={users} onSave={async (patch) => { const next = await updateTenant(tenant.id, patch); toast.push("success", "Saved."); setTenant(next); }} />}
      {tab === "users" && <UsersTab tenant={tenant} users={users} reload={reload} />}
      {tab === "numbers" && <NumbersTab tenant={tenant} />}
      {tab === "usage" && <UsageTab tenant={tenant} users={users} />}
      {tab === "plan" && <PlanTab tenant={tenant} reload={reload} />}
      {tab === "billing" && <BillingTab tenant={tenant} reload={reload} />}
      {tab === "audit" && <AuditTab audit={audit} />}
      {tab === "danger" && <DangerTab tenant={tenant} reload={reload} />}
    </div>
  );
}

/**
 * Resend the owner's set-password link.
 *
 * Company creation has been telling operators to "use Resend invite on the
 * company page" with nothing behind it — the invite endpoint creates a login
 * and 409s on someone who already has one, which is exactly this person. An
 * owner whose invite bounced or expired had no way back in.
 */
function ResendInviteButton({ tenantId, user }: { tenantId: string; user: TenantUser }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await resendUserInvite(tenantId, user.id);
      toast.push("success", `Set-password link sent to ${res.email}. It's valid for 7 days.`);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Couldn't send that. Try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      className="btn btn-ghost btn-sm"
      style={{ marginLeft: 8 }}
      onClick={onClick}
      disabled={busy}
      title={`Email ${user.email} a fresh link to set their password`}
    >
      {busy ? "Sending…" : "Resend invite"}
    </button>
  );
}

/* ============ OVERVIEW ============ */
function OverviewTab({ tenant, users, onSave }: { tenant: Tenant; users: TenantUser[]; onSave: (patch: Partial<Tenant>) => void }) {
  const [notes, setNotes] = useState(tenant.notes);
  const dirty = notes !== tenant.notes;

  const owners = users.filter((u) => u.role === "owner");
  const limits = tenant.effectiveLimits;
  const usage = tenant.usage;
  // No "Seats" meter: users are unlimited, so there is nothing to fill up.
  const meters: { label: string; used: number; max: number; format?: (n: number) => string }[] = [
    { label: "Numbers", used: usage.numbers, max: limits.maxNumbers },
    { label: "Customers", used: usage.customers, max: limits.maxCustomers },
    { label: "Voice min/mo", used: usage.voiceMinutesThisMonth, max: limits.voiceMinutesPerMonth },
    { label: "WhatsApp/mo", used: usage.whatsappMessagesThisMonth, max: limits.whatsappMessagesPerMonth },
    { label: "AI tokens/mo", used: usage.aiTokensThisMonth, max: limits.aiTokensPerMonth, format: (n) => Intl.NumberFormat("en-ZA", { notation: "compact" }).format(n) }
  ];

  return (
    <div className="grid-2">
      <div className="card">
        <div className="section-head"><h2>Snapshot</h2></div>
        <dl className="detail-list">
          <dt>Owner</dt>
          <dd>
            {owners[0]?.fullName ?? tenant.primaryContactEmail}
            {owners[0] ? <ResendInviteButton tenantId={tenant.id} user={owners[0]} /> : null}
          </dd>
          <dt>Contact email</dt><dd><a href={`mailto:${tenant.primaryContactEmail}`}>{tenant.primaryContactEmail}</a></dd>
          <dt>Contact phone</dt><dd className="mono">{tenant.primaryContactPhone}</dd>
          <dt>Twilio subaccount</dt><dd className="mono">{tenant.twilioSubaccountSid ?? "—"}</dd>
          <dt>Users</dt><dd className="tnum">{Intl.NumberFormat("en-ZA").format(usage.users)} <span className="muted">· unlimited</span></dd>
          <dt>Created</dt><dd>{new Date(tenant.createdAt).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}</dd>
          <dt>Last active</dt><dd>{tenant.lastActiveAt ? new Date(tenant.lastActiveAt).toLocaleString("en-ZA") : "never"}</dd>
          <dt>Tags</dt>
          <dd>
            {tenant.tags.length === 0 ? <span className="muted">No tags</span> : (
              <div className="row wrap" style={{ gap: 6 }}>
                {tenant.tags.map((tag) => <span key={tag} className="pill">{tag}</span>)}
              </div>
            )}
          </dd>
        </dl>
      </div>

      <div className="card">
        <div className="section-head"><h2>Usage vs limits</h2></div>
        <div className="col" style={{ gap: 12 }}>
          {meters.map((m) => {
            const pct = Math.min(100, (m.used / m.max) * 100);
            const cls = pct >= 95 ? "danger" : pct >= 80 ? "warn" : "";
            const fmt = m.format ?? ((n: number) => Intl.NumberFormat("en-ZA").format(n));
            return (
              <div key={m.label} className="meter-row">
                <div className="meter-label">{m.label}</div>
                <div className={`meter ${cls}`}><span style={{ width: `${pct}%` }} /></div>
                <div className="meter-value tnum">{fmt(m.used)} / {fmt(m.max)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ gridColumn: "1 / -1" }}>
        <div className="section-head">
          <h2>Internal notes</h2>
          {dirty && (
            <button className="btn btn-primary btn-sm" onClick={() => onSave({ notes })}>
              <Save size={14} /> Save
            </button>
          )}
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Context the support team should see — preferences, escalations, blockers…"
        />
      </div>
    </div>
  );
}

/* ============ USERS ============ */
function UsersTab({ tenant, users, reload }: { tenant: Tenant; users: TenantUser[]; reload: () => void }) {
  const toast = useToast();
  const [showInvite, setShowInvite] = useState(false);
  const [showBulkReps, setShowBulkReps] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<TenantUser | null>(null);

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="section-head" style={{ padding: "16px 22px" }}>
        <h2>Employees ({users.length})</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkReps(true)}>
            <UsersRound size={14} /> Invite reps in bulk
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>
            <Plus size={14} /> Invite employee
          </button>
        </div>
      </div>
      <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Number</th>
              <th>Last login</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} onClick={(e) => e.stopPropagation()}>
                <td>
                  <div style={{ fontWeight: 600 }}>{u.fullName}</div>
                  {!u.acceptedAt && <div className="pill warn" style={{ marginTop: 4 }}>Invite pending</div>}
                  {!u.active && <div className="pill danger" style={{ marginTop: 4 }}>Disabled</div>}
                </td>
                <td className="muted">{u.email}</td>
                <td>
                  <select
                    value={u.role}
                    onChange={async (e) => {
                      await updateUserRole(u.id, e.target.value as TenantUserRole);
                      toast.push("success", "Role updated.");
                      reload();
                    }}
                    style={{ maxWidth: 150 }}
                  >
                    {(["owner", "admin", "manager", "sales_agent", "auditor"] as const).map((r) => (
                      <option key={r} value={r}>{r.replace("_", " ")}</option>
                    ))}
                  </select>
                </td>
                <td className="mono">{u.assignedVoiceNumber ?? "—"}</td>
                <td className="muted nowrap">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString("en-ZA") : "never"}</td>
                <td className="row-actions">
                  <button
                    className="icon-btn" title="Send password reset"
                    onClick={async () => {
                      try {
                        const r = await sendPasswordReset(u.id);
                        if (r.sent) {
                          toast.push("success", `Reset email sent to ${u.email}.`);
                        } else if (r.timedOut) {
                          toast.push("error", `Couldn't confirm the reset email to ${u.email} went out (SMTP slow). It may still arrive — check before retrying.`);
                        } else {
                          toast.push("error", `Reset email to ${u.email} was NOT sent. Check auth/integration service logs.`);
                        }
                      } catch {
                        toast.push("error", `Reset request for ${u.email} failed.`);
                      }
                    }}
                  >
                    <KeyRound size={14} />
                  </button>
                  <button
                    className="icon-btn" title="Remove"
                    onClick={() => setConfirmRemove(u)}
                    style={{ color: "var(--danger)" }}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <InviteUserModal
          tenantId={tenant.id}
          onClose={() => setShowInvite(false)}
          onInvited={(created) => {
            setShowInvite(false);
            reload();
            if (created.inviteEmailSent === false) {
              toast.push(
                "error",
                created.email + " was created, but the set-password email couldn't be sent. Use “Reset password” to retry."
              );
            } else {
              toast.push("success", "Invite emailed to " + created.email + ".");
            }
          }}
        />
      )}
      {showBulkReps && (
        <BulkInviteRepsModal
          tenantId={tenant.id}
          tenantName={tenant.name}
          onClose={() => setShowBulkReps(false)}
          onDone={() => reload()}
        />
      )}
      <ConfirmDialog
        open={!!confirmRemove}
        title="Remove employee"
        message={<>This will revoke <strong>{confirmRemove?.email}</strong>'s access immediately. Their call history stays.</>}
        danger
        confirmLabel="Remove"
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          if (!confirmRemove) return;
          await removeUser(confirmRemove.id);
          setConfirmRemove(null);
          reload();
          toast.push("success", "Employee removed.");
        }}
      />
    </div>
  );
}

function InviteUserModal({ tenantId, onClose, onInvited }: { tenantId: string; onClose: () => void; onInvited: (created: { email: string; inviteEmailSent?: boolean }) => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ fullName: "", email: "", role: "sales_agent" as TenantUserRole, message: "" });
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Invite employee</h2>
        <p>They'll get an email with a link to set their password — no temporary password is created.</p>
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              const created = await inviteUser(tenantId, form);
              onInvited(created);
            } catch (err) {
              toast.push("error", err instanceof Error ? err.message : "Couldn't send the invite.");
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="form-grid">
            <label>Full name<input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></label>
            <label>Email<input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label>
              Role
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as TenantUserRole })}>
                {(["owner", "admin", "manager", "sales_agent", "auditor"] as const).map((r) => (
                  <option key={r} value={r}>{r.replace("_", " ")}</option>
                ))}
              </select>
            </label>
          </div>
          <label style={{ display: "block", marginTop: 12 }}>
            Message (optional)
            <textarea
              rows={3}
              maxLength={2000}
              placeholder="Add a note for them. It'll be included in their set-password email."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              style={{ width: "100%", resize: "vertical" }}
            />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}><Mail size={14} /> Send invite</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Bulk rep onboarding. Built for the "client sent me their whole field team and
 * training starts in an hour" case.
 *
 * Two things worth knowing while using it:
 *  - Each invite blocks on an SMTP send (~7s) and they run 3 at a time, so 15
 *    reps takes ~40s. The modal stays open and reports per-row, rather than
 *    firing and hoping.
 *  - Re-sending invalidates earlier links. auth-service consumes a user's live
 *    tokens whenever it mints a new one, so if you run this twice, only the
 *    newest email works. Send it once, close to the session.
 */
function BulkInviteRepsModal({
  tenantId, tenantName, onClose, onDone
}: { tenantId: string; tenantName: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [raw, setRaw] = useState("");
  const [team, setTeam] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<BulkInviteSummary | null>(null);

  // Mirror the server's parser closely enough to give a live count. The server
  // is authoritative (it also dedupes against existing reps).
  const detected = useMemo(() => {
    const seen = new Set<string>();
    for (const chunk of raw.split(/[\r\n,;]+/)) {
      const t = chunk.trim();
      if (!t) continue;
      const angled = t.match(/^(.*?)<\s*([^<>\s]+@[^<>\s]+)\s*>$/);
      const email = (angled ? angled[2] : t).trim().toLowerCase();
      if (email.includes("@") && !/\s/.test(email)) seen.add(email);
    }
    return seen.size;
  }, [raw]);

  const run = async () => {
    setBusy(true);
    try {
      const res = await bulkInviteReps(tenantId, {
        raw,
        team: team.trim() || undefined,
        message: message.trim() || undefined
      });
      setSummary(res);
      onDone();
      if (res.failed > 0) {
        toast.push("error", `${res.sent} of ${res.total} invited. ${res.failed} need attention.`);
      } else if (res.unknown > 0) {
        toast.push("success", `${res.sent} invited, ${res.unknown} unconfirmed (the send timed out).`);
      } else {
        toast.push("success", `Invited ${res.sent} rep${res.sent === 1 ? "" : "s"} at ${tenantName}.`);
      }
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "The bulk invite failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h2>Invite reps in bulk</h2>
        <p>
          Paste the rep email addresses. Each one gets a sales-rep profile, a login, and an
          email with a link to set their own password. Existing reps are re-invited, not duplicated.
        </p>

        {summary ? (
          <>
            <div className="row" style={{ gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
              <span className="pill success">{summary.sent} emailed</span>
              {summary.created > 0 && <span className="pill">{summary.created} new profile{summary.created === 1 ? "" : "s"}</span>}
              {summary.reinvited > 0 && <span className="pill">{summary.reinvited} existing</span>}
              {summary.unknown > 0 && <span className="pill warn">{summary.unknown} unconfirmed</span>}
              {summary.failed > 0 && <span className="pill danger">{summary.failed} failed</span>}
            </div>
            <div className="table-wrap" style={{ maxHeight: 320, overflow: "auto" }}>
              <table className="table">
                <tbody>
                  {summary.results.map((row) => (
                    <tr key={row.email}>
                      <td style={{ width: 22 }}>
                        {row.invite === "sent" ? (
                          <CheckCircle2 size={15} style={{ color: "var(--success)" }} />
                        ) : row.invite === "unknown" ? (
                          <HelpCircle size={15} style={{ color: "var(--warn)" }} />
                        ) : (
                          <XCircle size={15} style={{ color: "var(--danger)" }} />
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{row.fullName}</div>
                        <div className="faint" style={{ fontSize: 11 }}>{row.email}</div>
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        {row.error
                          ? row.error
                          : row.invite === "unknown"
                            ? "Send timed out — the email may still arrive. Check before re-sending."
                            : row.profile === "created"
                              ? "Profile created, invite sent"
                              : "Re-invited"}
                        {row.agentId && !row.linked ? " (login not linked — tell Jared)" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => { setSummary(null); setRaw(""); }}>
                Invite more
              </button>
              <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <form
            className="form"
            onSubmit={(e) => { e.preventDefault(); if (!busy && detected > 0) void run(); }}
          >
            <label>
              Rep emails
              <textarea
                rows={7}
                autoFocus
                placeholder={"jane@client.co.za\nsipho@client.co.za\nJared M <jared@client.co.za>"}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                style={{ width: "100%", resize: "vertical", fontFamily: "var(--mono, monospace)", fontSize: 13 }}
              />
              <span className="faint" style={{ fontSize: 11 }}>
                One per line, or comma separated. {detected > 0 ? `${detected} address${detected === 1 ? "" : "es"} detected.` : "Names are taken from the address unless you use Name <email>."}
              </span>
            </label>
            <label style={{ display: "block", marginTop: 12 }}>
              Team (optional)
              <input value={team} onChange={(e) => setTeam(e.target.value)} placeholder="e.g. Field Sales" />
            </label>
            <label style={{ display: "block", marginTop: 12 }}>
              Message (optional)
              <textarea
                rows={3}
                maxLength={2000}
                placeholder="Added to every invite email. e.g. Training is Friday at 10am, install the app before we start."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                style={{ width: "100%", resize: "vertical" }}
              />
            </label>
            <p className="faint" style={{ fontSize: 11.5, marginTop: 12 }}>
              Sending takes about 3 seconds per rep, so keep this window open. Re-sending later
              invalidates the earlier links, so send once, close to when they'll use it.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={busy || detected === 0}>
                <Mail size={14} /> {busy ? `Inviting ${detected}…` : `Invite ${detected || ""} rep${detected === 1 ? "" : "s"}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * One package on the picker. Shows what the customer will actually be able to
 * reach, read live from config-service's flag mapping rather than from the
 * marketing copy - the description sells it, the ticks are what they get.
 */
function PackCard({
  pack,
  current,
  busy,
  onPick
}: {
  pack: PackOption;
  current: boolean;
  busy: boolean;
  onPick: (slug: string) => void;
}) {
  const includes = packIncludes(pack);
  const excludes = packExcludes(pack);
  return (
    <button
      disabled={busy}
      onClick={() => onPick(pack.pack)}
      className="card card-hover"
      style={{
        padding: 14,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        cursor: busy ? "default" : "pointer",
        borderColor: current ? "var(--accent)" : undefined,
        background: current ? "var(--accent-soft)" : undefined,
        textAlign: "left"
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ color: "var(--text)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          {pack.label}
          {current && <span className="pill success">Current</span>}
          {pack.flatMonthlyCents != null && <span className="pill">Whole business</span>}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {pack.flatMonthlyCents != null
            ? "Unlimited users"
            : pack.perRepMonthlyCents != null
              ? "No rep limit"
              : "Quoted, unlimited users"}{" "}
          · {pack.description}
        </div>
        {includes.length ? (
          <div className="muted" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
            <strong style={{ color: "var(--text)" }}>Unlocks:</strong> {includes.join(" · ")}
            {excludes.length ? (
              <>
                <br />
                <span style={{ opacity: 0.8 }}>No: {excludes.join(" · ")}</span>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="tnum" style={{ whiteSpace: "nowrap" }}>{formatPackPrice(pack)}</div>
    </button>
  );
}

/**
 * Per-tenant module switches, on top of whatever the package grants.
 *
 * This exists because a package is the only lever the console had, and some
 * modules are deliberately in no package at all - Automation is off by default
 * and in no pack until its pricing is decided, and pay links are held back on a
 * legal precondition. Without this card the only way to run a pilot was a
 * hand-written row in config-service's database.
 *
 * The "From" tag is not decoration: an operator needs to see whether a module
 * is on because the customer's package includes it, or because somebody
 * switched it on for this one company. Clearing an override is not the same as
 * switching a module off, so both are offered separately.
 */
function ModulesCard({ tenant }: { tenant: Tenant }) {
  const toast = useToast();
  const [flags, setFlags] = useState<TenantFlag[] | null>(null);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = React.useCallback(() => {
    fetchTenantFlags(tenant.id)
      .then((r) => {
        setFlags((r.items || []).filter((f) => f.category === "Features" && f.type === "boolean"));
        setError("");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't load this company's modules"));
  }, [tenant.id]);

  useEffect(load, [load]);

  async function apply(key: string, work: () => Promise<unknown>, done: string) {
    setBusyKey(key);
    try {
      await work();
      toast.push("success", done);
      load();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't change that module.");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="card">
      <div className="section-head">
        <h2>Modules</h2>
        <span className="muted">On top of the package. Overrides win.</span>
      </div>
      {error ? <p className="error">{error}</p> : null}
      {!flags ? (
        <p className="muted">Loading...</p>
      ) : (
        <div className="col" style={{ gap: 10 }}>
          {flags.map((f) => {
            const on = f.effective === true;
            return (
              <div key={f.key} className="row" style={{ gap: 10, alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={busyKey === f.key}
                  onChange={() =>
                    apply(
                      f.key,
                      () => setTenantFlag(tenant.id, f.key, on ? "false" : "true"),
                      on ? "Module switched off for this company." : "Module switched on for this company."
                    )
                  }
                  style={{ marginTop: 3 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {f.key}{" "}
                    <span className="faint" style={{ fontWeight: 400, fontSize: 11.5 }}>
                      from {f.source}
                    </span>
                  </div>
                  <div className="faint" style={{ fontSize: 11.5 }}>{f.description}</div>
                </div>
                {f.isTenantOverride ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busyKey === f.key}
                    onClick={() =>
                      apply(f.key, () => resetTenantFlag(tenant.id, f.key), "Override cleared; back to their package.")
                    }
                  >Clear override</button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============ PLAN & LIMITS ============ */
function PlanTab({ tenant, reload }: { tenant: Tenant; reload: () => void }) {
  const toast = useToast();
  const { packs } = usePacks();
  const [overrides, setOverrides] = useState<Partial<PlanLimits>>(tenant.customLimits);
  const [busy, setBusy] = useState(false);
  const [pickBusy, setPickBusy] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const defaults = PLANS[tenant.plan].defaultLimits;

  // The four packages on the marketing site are the ones an operator should be
  // choosing from; the nine retired tiers are folded away so they cannot be
  // picked by accident. A company already sitting on a retired tier always sees
  // that section open, otherwise its "Current" badge would be invisible.
  const live = packs.filter(isSellable);
  // A reseller's client workspace has its own pack (tenant plan phase 9): not
  // sold, not retired, so it sits in its own section rather than in either.
  const clientPacks = packs.filter((p) => isClientPack(p));
  const legacy = packs.filter((p) => !isSellable(p) && !isClientPack(p));
  const currentIsLegacy = legacy.some((p) => p.pack === tenant.pack);
  const legacyOpen = showLegacy || currentIsLegacy;

  async function pickPack(pack: string) {
    if (pack === tenant.pack || pickBusy) return;
    setPickBusy(true);
    try {
      await changeTenantPack(tenant.id, pack);
      toast.push("success", "Package updated.");
      reload();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't change the package.");
    } finally {
      setPickBusy(false);
    }
  }

  // No seats field: users are unlimited on every plan.
  const LIMIT_FIELDS: { key: keyof PlanLimits; label: string; suffix?: string }[] = [
    { key: "maxNumbers", label: "Max Twilio numbers" },
    { key: "maxCustomers", label: "Max customers" },
    { key: "voiceMinutesPerMonth", label: "Voice minutes / month" },
    { key: "whatsappMessagesPerMonth", label: "WhatsApp messages / month" },
    { key: "aiTokensPerMonth", label: "AI tokens / month" }
  ];

  return (
    <div className="grid-2">
      <div className="card">
        <div className="section-head">
          <h2>Package</h2>
          <span className="muted">Sets the tenant's features and per-rep price.</span>
        </div>
        <div className="col" style={{ gap: 14 }}>
          <div className="col" style={{ gap: 8 }}>
            {live.map((p) => (
              <PackCard key={p.pack} pack={p} current={tenant.pack === p.pack} busy={pickBusy} onPick={pickPack} />
            ))}
          </div>

          {clientPacks.length ? (
            <div>
              <p className="muted" style={{ fontSize: 12, margin: "4px 0 8px" }}>
                For a reseller&apos;s client: every module except paid telephony, unlimited users, never billed to the
                client. Switch voice on for one client from Modules.
              </p>
              <div className="col" style={{ gap: 8 }}>
                {clientPacks.map((p) => (
                  <PackCard key={p.pack} pack={p} current={tenant.pack === p.pack} busy={pickBusy} onPick={pickPack} />
                ))}
              </div>
            </div>
          ) : null}

          {legacy.length ? (
            <div>
              <button
                type="button"
                className="button-ghost"
                style={{ fontSize: 12 }}
                onClick={() => setShowLegacy((v) => !v)}
                disabled={currentIsLegacy}
                title={currentIsLegacy ? "This company is on a legacy package, so they stay visible." : undefined}
              >
                {legacyOpen ? "Hide" : "Show"} {legacy.length} retired package{legacy.length === 1 ? "" : "s"}
              </button>
              {legacyOpen ? (
                <>
                  <p className="muted" style={{ fontSize: 12, margin: "8px 0 8px" }}>
                    Retired tiers. They still resolve so existing tenants keep exactly the features they
                    were sold, but nobody new should be put on one.
                  </p>
                  <div className="col" style={{ gap: 8 }}>
                    {legacy.map((p) => (
                      <PackCard key={p.pack} pack={p} current={tenant.pack === p.pack} busy={pickBusy} onPick={pickPack} />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <ModulesCard tenant={tenant} />

      <div className="card">
        <div className="section-head">
          <h2>Custom limits</h2>
          <span className="muted">Overrides apply on top of the plan defaults.</span>
        </div>
        <div className="form">
          {LIMIT_FIELDS.map((f) => {
            const val = overrides[f.key] ?? defaults[f.key];
            const isOverridden = overrides[f.key] != null && overrides[f.key] !== defaults[f.key];
            return (
              <label key={f.key as string}>
                {f.label}
                <div className="row">
                  <input
                    type="number"
                    min={0}
                    value={val}
                    onChange={(e) => setOverrides({ ...overrides, [f.key]: Number(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  {isOverridden && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        const { [f.key]: _, ...rest } = overrides;
                        setOverrides(rest);
                      }}
                    >Reset</button>
                  )}
                </div>
                <span className="faint" style={{ fontSize: 11.5 }}>
                  Plan default: {Intl.NumberFormat("en-ZA").format(defaults[f.key])}
                </span>
              </label>
            );
          })}
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await overrideTenantLimits(tenant.id, overrides);
                toast.push("success", "Custom limits saved.");
                reload();
              } finally { setBusy(false); }
            }}
          >
            <Save size={14} /> Save custom limits
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ BILLING ============ */
function BillingTab({ tenant, reload }: { tenant: Tenant; reload: () => void }) {
  const toast = useToast();
  const prompt = usePrompt();
  const b = tenant.billing;
  return (
    <>
      <div className="grid-2">
        <div className="card">
          <div className="section-head"><h2>Status</h2></div>
          <dl className="detail-list">
            <dt>State</dt>
            <dd>
              <span className={`pill ${b.status === "active" ? "success" : b.status === "past_due" ? "danger" : b.status === "comped" ? "accent" : "info"}`}>{b.status}</span>
            </dd>
            <dt>Next invoice</dt><dd>{b.nextInvoiceAt ? new Date(b.nextInvoiceAt).toLocaleDateString("en-ZA") : "—"}</dd>
            <dt>Trial ends</dt><dd>{b.trialEndsAt ? new Date(b.trialEndsAt).toLocaleDateString("en-ZA") : "—"}</dd>
            <dt>Monthly charge</dt><dd className="tnum">{b.monthlyAmountCents != null ? formatZAR(b.monthlyAmountCents) : "—"}</dd>
            <dt>Billing per employee</dt>
            <dd className="tnum">
              {(() => {
                const seats = b.subscribedReps && b.subscribedReps > 0 ? b.subscribedReps : 1;
                const perSeat = b.monthlyAmountCents != null ? Math.round(b.monthlyAmountCents / seats) : null;
                return perSeat != null
                  ? `${seats} rep${seats === 1 ? "" : "s"} × ${formatZAR(perSeat)}`
                  : `${seats} rep${seats === 1 ? "" : "s"} · no charge set`;
              })()}
            </dd>
            <dt>Balance</dt><dd className="tnum">{formatZAR(b.balanceCents)}</dd>
            <dt>Stripe customer</dt><dd className="mono">{b.stripeCustomerId ?? "—"}</dd>
          </dl>
        </div>
        <div className="card">
          <div className="section-head">
            <h2>Manual overrides</h2>
            <span className="muted">Use only when automated billing can't.</span>
          </div>
          <div className="col" style={{ gap: 10 }}>
            <button className="btn btn-ghost" onClick={async () => { await markInvoicePaid(tenant.id); toast.push("success", "Marked paid."); reload(); }}>
              Mark outstanding invoices as paid
            </button>
            <button className="btn btn-ghost" onClick={async () => { await extendTrial(tenant.id, 14); toast.push("success", "Trial extended 14 days."); reload(); }}>
              Extend trial by 14 days
            </button>
            <button className="btn btn-ghost" onClick={async () => {
              const reason = await prompt({
                title: "Comp tenant",
                message: <>Give <strong>{tenant.name}</strong> a free account. Recorded in the audit log.</>,
                label: "Reason for comp",
                placeholder: "e.g. Partner / beta agreement",
                confirmLabel: "Comp tenant",
                required: true
              });
              if (!reason) return;
              await compTenant(tenant.id, reason);
              toast.push("success", "Tenant comped.");
              reload();
            }}>
              Comp tenant (free account)
            </button>
          </div>
        </div>
      </div>
      <PriceOverrideCard tenant={tenant} reload={reload} />
      <DebitOrdersCard tenantId={tenant.id} reload={reload} />
    </>
  );
}

function PriceOverrideCard({ tenant, reload }: { tenant: Tenant; reload: () => void }) {
  const toast = useToast();
  const current = tenant.billing.monthlyAmountCents ?? null;
  const [rand, setRand] = useState(current != null ? String(current / 100) : "");
  const [busy, setBusy] = useState(false);

  const parsedCents: number | null = (() => {
    const t = rand.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
  })();
  const invalid = Number.isNaN(parsedCents);
  const dirty = !invalid && parsedCents !== current;

  async function save() {
    if (invalid) { toast.push("error", "Enter a valid amount in Rand."); return; }
    setBusy(true);
    try {
      await setTenantMonthlyAmount(tenant.id, parsedCents);
      toast.push("success", parsedCents == null ? "Custom price cleared." : "Custom monthly price saved.");
      reload();
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't save price.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="section-head">
        <h2>Custom monthly price</h2>
        <span className="muted">Overrides the per-rep list price — this is what the monthly debit collects. Use it to lock in a negotiated deal or discount.</span>
      </div>
      <div className="row" style={{ gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ flex: "0 0 240px" }}>
          Monthly charge (ZAR)
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <span className="muted">R</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={rand}
              onChange={(e) => setRand(e.target.value)}
              placeholder="e.g. 7999"
              style={{ flex: 1 }}
            />
          </div>
        </label>
        <button className="btn btn-primary" disabled={busy || !dirty} onClick={save}>
          <Save size={14} /> Save price
        </button>
        {current != null && (
          <button className="btn btn-ghost" disabled={busy} onClick={() => setRand("")}>
            Clear override
          </button>
        )}
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        {current != null ? (
          <>Currently billing <strong>{formatZAR(current)}</strong> / month. Clear the field and save to remove the override and fall back to the plan's per-rep amount.</>
        ) : (
          <>No custom price set — billing uses the plan's per-rep amount × reps. Enter an amount to lock a fixed monthly price for this tenant.</>
        )}
      </p>
    </div>
  );
}

function DebitOrdersCard({ tenantId, reload }: { tenantId: string; reload: () => void }) {
  const toast = useToast();
  const prompt = usePrompt();
  const [orders, setOrders] = useState<DebitOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    listDebitOrders(tenantId).then((rows) => {
      if (alive) { setOrders(rows); setLoading(false); }
    });
    return () => { alive = false; };
  }, [tenantId]);

  function statusPill(s: DebitOrder["status"]) {
    if (s === "successful") return "success";
    if (s === "failed" || s === "abandoned") return "danger";
    if (s === "retrying") return "warning";
    if (s === "submitted") return "info";
    return "muted";
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="section-head">
        <h2>Debit orders</h2>
        <span className="muted">Monthly bank collections. Failed attempts auto-retry up to 3x.</span>
      </div>
      {loading ? (
        <div className="muted" style={{ padding: 12 }}>Loading…</div>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <h3>No debit orders yet</h3>
          <p>Once Stripe / EFT mandate is in place, collection attempts show here.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Scheduled</th>
              <th>Amount</th>
              <th>Collected</th>
              <th>Status</th>
              <th>Retries</th>
              <th>Reason</th>
              <th>Bank ref</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td>{new Date(o.periodMonth).toLocaleDateString("en-ZA", { year: "numeric", month: "short" })}</td>
                <td>{new Date(o.scheduledFor).toLocaleDateString("en-ZA")}</td>
                <td className="tnum">{formatZAR(o.amountCents)}</td>
                <td className="tnum">{formatZAR(o.collectedCents)}</td>
                <td><span className={`pill ${statusPill(o.status)}`}>{o.status}</span></td>
                <td>{o.retries}</td>
                <td className="muted">{o.failureReason ?? "—"}</td>
                <td className="mono">{o.bankReference ?? "—"}</td>
                <td>
                  {(o.status === "failed" || o.status === "retrying") && (
                    <button className="btn btn-ghost btn-xs" onClick={async () => {
                      await retryDebitOrder(o.id);
                      toast.push("success", "Retry queued.");
                      const next = await listDebitOrders(tenantId);
                      setOrders(next);
                      reload();
                    }}>Retry</button>
                  )}
                  {o.status !== "successful" && o.status !== "abandoned" && (
                    <button className="btn btn-ghost btn-xs" onClick={async () => {
                      const ref = await prompt({
                        title: "Mark debit order paid",
                        message: "Record a manual (off-platform) payment for this debit order.",
                        label: "Bank reference",
                        placeholder: "e.g. EFT-20260608-001",
                        confirmLabel: "Mark paid",
                        required: true
                      });
                      if (!ref) return;
                      await markDebitOrderPaid(o.id, ref);
                      toast.push("success", "Marked paid.");
                      const next = await listDebitOrders(tenantId);
                      setOrders(next);
                      reload();
                    }}>Mark paid</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============ AUDIT ============ */
function AuditTab({ audit }: { audit: AuditEvent[] }) {
  if (audit.length === 0) {
    return (
      <div className="card empty-state">
        <h3>No audit events yet</h3>
        <p>Actions you take on this tenant will appear here.</p>
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      {audit.map((e) => (
        <div key={e.id} className="audit-entry">
          <div className="time">{new Date(e.occurredAt).toLocaleString("en-ZA")}</div>
          <div>
            <div className="who">{e.actorEmail}</div>
            <div className="what">{e.summary}</div>
          </div>
          <span className="pill accent">{e.type}</span>
        </div>
      ))}
    </div>
  );
}

/* ============ DANGER ============ */
function DangerTab({ tenant, reload }: { tenant: Tenant; reload: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const prompt = usePrompt();

  async function onSuspend() {
    const reason = await prompt({
      title: "Suspend tenant",
      message: <>This blocks <strong>{tenant.name}</strong> from logging in and pauses new calls/messages. Data is preserved.</>,
      label: "Reason (audited)",
      placeholder: "Manual suspension",
      confirmLabel: "Suspend",
      danger: true
    });
    if (reason === null) return;
    await suspendTenant(tenant.id, reason.trim() || "Manual suspension");
    toast.push("success", "Tenant suspended.");
    reload();
  }

  async function onDelete() {
    const typed = await prompt({
      title: "Delete tenant",
      message: <>This permanently removes <strong>{tenant.name}</strong> &mdash; every user, call, chat, and recording. This cannot be undone.</>,
      label: <>Type <strong>{tenant.name}</strong> to confirm</>,
      placeholder: tenant.name,
      matchText: tenant.name,
      confirmLabel: "Delete forever",
      danger: true
    });
    if (typed === null) return;
    await deleteTenant(tenant.id);
    toast.push("success", "Tenant deleted.");
    navigate("/companies");
  }

  return (
    <div className="card" style={{ borderColor: "rgba(255,92,122,0.3)" }}>
      <h2 style={{ color: "var(--danger)" }}>Danger zone</h2>
      <p>These actions are audited and difficult to reverse.</p>
      <div className="col" style={{ gap: 12, marginTop: 14 }}>
        {tenant.status !== "suspended" ? (
          <DangerRow
            title="Suspend tenant"
            body="Blocks logins and pauses voice/WhatsApp ingestion. Data is preserved."
            actionLabel="Suspend"
            onAction={onSuspend}
          />
        ) : (
          <DangerRow
            title="Unsuspend tenant"
            body="Restores access and resumes ingestion."
            actionLabel="Unsuspend"
            variant="primary"
            onAction={async () => { await unsuspendTenant(tenant.id); toast.push("success", "Unsuspended."); reload(); }}
          />
        )}
        <DangerRow
          title="Delete tenant"
          body="Permanently removes the tenant, all users, calls, chats, and recordings. Cannot be undone."
          actionLabel="Delete forever"
          onAction={onDelete}
        />
      </div>
    </div>
  );
}

function DangerRow({ title, body, actionLabel, onAction, variant }: { title: string; body: string; actionLabel: string; onAction: () => void; variant?: "primary" | "danger" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "14px 16px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
      <div>
        <div style={{ color: "var(--text)", fontWeight: 600 }}>{title}</div>
        <div className="muted">{body}</div>
      </div>
      <button className={`btn ${variant === "primary" ? "btn-primary" : "btn-danger"} btn-sm`} onClick={onAction}>
        <Power size={13} /> {actionLabel}
      </button>
    </div>
  );
}
